import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { FolderWithElements } from "@/types/base/folder_with_elements"
import { getFolderById } from "@/db/folders"
import { withRetry } from "@/utils/retry_utils"
import { v4 as uuidv4 } from "uuid"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('realtime.folders', supabase)

type Row = Tables<'folders'>
type EleRow = Tables<'folder_elements'>

const appliesToWorkspace = async (workspaceId:string | null, folderId: string): Promise<boolean> => {
	if (!workspaceId) return false
	const folderWithElements = await withRetry<FolderWithElements>(() => getFolderById(supabase, folderId))
	return folderWithElements?.workspace_id === workspaceId
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 * @param workspaceId
 * @param setFolders
 * @returns
 */
export function makeFolderChangeHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setFolders: Dispatch<SetStateAction<FolderWithElements[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `folder-changes-${uuid}`,
		channelName: `folder-changes-${uuid}`,
		events: ['INSERT', 'UPDATE', 'DELETE'],
		schema: 'public',
		table: 'folders',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") { //adding a new folder
				const folder = payload.new as Row
				if (folder.workspace_id === workspaceId) {
					setFolders(prevFolders => {
  					const exists = prevFolders.some(f => f.id === folder.id)
  					if (exists) return prevFolders // No change, no re-render
						logger.info(`Handling folder Insert:${printObject(folder)}`)
						const folderWithElements: FolderWithElements = {
							...folder,
							folder_elements: [] // Initialize as an empty array
						}
  					return [...prevFolders, folderWithElements]
					})
				}
			}
			else if (payload.eventType === 'UPDATE') { //updating an existing folder (rename essentially)
				const folder = payload.new as Row
				if (folder.workspace_id === workspaceId) {
					const folderWithElements = await withRetry<FolderWithElements>(() => getFolderById(supabase, folder.id))
					setFolders(prevItems => {
						const exists = prevItems.some(f => f.id === folder.id)
						if (!exists) return prevItems // No change, no re-render
						logger.info(`Handling folder Insert:${folder.id}`)
						return prevItems.map(prevItem => prevItem.id === folder.id ? folderWithElements : prevItem)
					})
				}
			}
			else if (payload.eventType == "DELETE") { //deleting an existing folder
				const folder = payload.old as Row
				if (folder.workspace_id === workspaceId) {
					setFolders(prevItems => {
						const exists = prevItems.some(f => f.id === folder.id)
						if (!exists) return prevItems // No change, no re-render
						logger.info(`Handling folder Delete:${folder.id}`)
						return prevItems.filter(prevItem => prevItem.id !== folder.id)
					})
				}
			}
		},
		inactivityThreshold: 1_800_000
	}
}

/**
 * This method doesn't handle update since the relationship join table is not designed to mutate in that way
 * @param workspaceId
 * @param setFolders
 * @returns
 */
export function makeFolderElementsHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setFolders: Dispatch<SetStateAction<FolderWithElements[]>>
): HandlerConfig<EleRow> {
	const uuid = uuidv4()
	return {
		key: `folder-ele-changes-${uuid}`,
		channelName: `folder-ele-changes-${uuid}`,
		events: ['INSERT', 'DELETE'],
		schema: 'public',
		table: 'folder_elements',
		handler: async (payload: RealtimePostgresChangesPayload<EleRow>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") {  //adding a new folder element
				const folderEle = payload.new as EleRow
				const id = folderEle.folder_id
				if (await appliesToWorkspace(workspaceId, id)) {
					const folderWithElements = await withRetry<FolderWithElements>(() => getFolderById(supabase, id))
					setFolders(prevItems => {
  					const exists = prevItems.some(f => f.id === id)  //do we already have the folder
  					if (!exists) return prevItems // Folder not found, no update
						logger.info(`Handling folder element Insert:${printObject(folderEle)}`)
						return prevItems.map(prevItem => prevItem.id === id ? folderWithElements : prevItem)
					})
				}
			}
			else if (payload.eventType == "DELETE") { //deleting a folder element
				const folderEle = payload.old as EleRow
				const id = folderEle.folder_id
				if (await appliesToWorkspace(workspaceId, id)) {
					const folderWithElements = await withRetry<FolderWithElements>(() => getFolderById(supabase, id))
					setFolders(prevItems => {
						const exists = prevItems.some(f => f.id === id)  //do we already have the folder
						if (!exists) return prevItems // Folder not found, no update
						logger.info(`Handling folder element Delete:${id}`)
						return prevItems.map((prevItem: any) => prevItem.id === id ? folderWithElements : prevItem)
					})
				}
			}
		},
		inactivityThreshold: 1_800_000
	}
}